import { Routes } from '.generated/templates/Project.Routes.model';
import { useSitecoreContext, Text } from '@sitecore-jss/sitecore-jss-nextjs';
import classnames from 'classnames';
import { LayoutPageSectionShell } from 'components/authorable/layout/LayoutPageSection/LayoutPageSection';
import Button from 'components/helpers/Button';
import { useCart } from 'components/helpers/Cart/CartHelper';
import { CartItem } from 'lib/util/cart';
import { useCallback, useEffect, useRef, useState } from 'react';
import CartEntry from './Components/CartEntry';
import { LayoutRightThirdShell } from 'components/authorable/layout/LayoutRightThird/LayoutRightThird';
import HeadingTag from 'components/helpers/HeadingTag';
import EmptyCart from './Components/EmptyCart';
import useWindowPosition from 'lib/util/useWindowPosition';
import useScreenWidth from 'lib/util/useScreenWidth';
import {
  EcommerceDataLayerObject,
  ProductItemRP,
  gtmEcommerceDatalayerPush,
  alwaysSuppressLinkDataLayerPush,
} from 'lib/util/gtm-datalayer';

const CartPage = () => {
  const { sitecoreContext } = useSitecoreContext();
  const { fields } = sitecoreContext.route as unknown as Routes.Fields.CartPage;
  const { cart, fetchCart } = useCart();
  const [cartItems, setCartItems] = useState<CartItem[]>(cart);
  const [isEmptyCart, setEmptyCart] = useState(cart.length === 0);

  const fetchUpdatedCart = useCallback(() => {
    const updatedCart = fetchCart();
    setEmptyCart(updatedCart.length === 0);
    setCartItems(updatedCart);
  }, [fetchCart]);

  useEffect(() => {
    fetchUpdatedCart();
    setCartItems(cart);
    setEmptyCart(cart.length <= 0);
  }, [cart, fetchUpdatedCart]);

  const cartLeftCol = (
    <>
      {cartItems?.map((item) => {
        return <CartEntry key={item.id} cartItem={item} fetchUpdatedCart={fetchUpdatedCart} />;
      })}
    </>
  );

  const [productHeader, setproductHeader] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const productHeaderElement = document.querySelector('.heading-products');
    if (productHeaderElement instanceof HTMLElement) {
      setproductHeader(productHeaderElement);
    }
  }, []);

  const divRef = useRef<HTMLDivElement>(null);
  const offsetValue = divRef.current?.getBoundingClientRect();
  const productHeaderRef = productHeader?.getBoundingClientRect();
  const screenWidth = useScreenWidth();

  const { y } = useWindowPosition();

  let style: {} = {
    boxShadow: '0px -3px 4px 0px rgba(0, 0, 0, 0.12)',
  };
  if (screenWidth > 767) {
    const isSticky =
      offsetValue && y > offsetValue.top && productHeaderRef && y > productHeaderRef?.bottom;

    style = {
      position: isSticky ? 'fixed' : 'static',
      top: isSticky ? 0 : 'auto',
      padding: isSticky ? '16px 0 0 0' : '0',
    };
  }

  const handleClickOnCheckout = () => {
    const items = cartItems.map((item, i) => {
      return {
        item_id: item.itemNumber ?? item.id,
        item_name: item.name,
        item_list_name: 'order materials',
        discount: 'N/A',
        coupon: 'N/A',
        index: i.toString(),
        item_brand: 'NMDP',
        item_variant: 'N/A',
        price: item.price,
        quantity: item.quantity,
      };
    }) as ProductItemRP[];

    const gmtData: EcommerceDataLayerObject = {
      event: 'begin_checkout',
      ecommerce: {
        value: '0',
        currency: 'USD',
        items: items,
      },
    };
    gtmEcommerceDatalayerPush(gmtData);
  };

  const cartRightCol = (
    <>
      <div className="fixedParent">
        <div
          ref={divRef}
          className={classnames(
            `md:bg-transparent`,
            `fixed`,
            `left-0`,
            `bottom-0`,
            `w-full`,
            `bg-white`,
            `px-[16px]`,
            `py-[21px]`,
            `md:left-auto`,
            `md:bottom-auto`,
            `md:bottom-auto`,
            `md:w-auto`,
            `md:px-[0px]`,
            `md:py-[0px]`,
            `md:!shadow-none`
          )}
          style={style || undefined}
        >
          <Button
            variant="secondary"
            linkField={fields?.continueShoppingLink}
            className={classnames('mb-[16px] w-full text-center md:w-auto lg:mr-[16px]')}
          />
          <Button
            variant="primary"
            linkField={fields?.checkoutLink}
            onGtmPush={alwaysSuppressLinkDataLayerPush}
            onClick={handleClickOnCheckout}
            className={classnames('mb-[16px] mr-[16px] w-full text-center md:w-auto')}
          />
        </div>
      </div>
    </>
  );
  return (
    <div data-component="authorable/pages/cartpage" className={classnames('relative', 'z-10')}>
      <LayoutPageSectionShell theme="theme-inherit" padding="tiny">
        <HeadingTag
          headingLevel="h1"
          className={classnames('heading-products mb-[18px] md:mb-[23px]')}
        >
          <Text field={fields?.cartLabel} tag="" />
        </HeadingTag>
        {!isEmptyCart && <LayoutRightThirdShell left={cartLeftCol} right={cartRightCol} />}
        {isEmptyCart && <EmptyCart fields={fields} />}
      </LayoutPageSectionShell>
    </div>
  );
};

export default CartPage;
